"use client";

import { EventCard } from "@/components/feed/event-card";
import { DemoBadge } from "@/components/feed/demo-badge";
import type { LightboxPhoto } from "@/components/feed/photo-lightbox";

export type FeedEvent = {
  id: string;
  garden_id: string;
  type: string;
  data: Record<string, unknown>;
  created_at: string;
  garden_name: string | null;
  first_name: string | null;
  is_demo?: boolean;
};

const GROUP_WINDOW_MS = 60 * 60 * 1000; // 1h

export function groupEvents(events: FeedEvent[]): FeedEvent[][] {
  const groups: FeedEvent[][] = [];
  for (const e of events) {
    const last = groups[groups.length - 1];
    const head = last?.[0];
    const sameGarden = head?.garden_id === e.garden_id;
    const close =
      head &&
      new Date(head.created_at).getTime() - new Date(e.created_at).getTime() <= GROUP_WINDOW_MS;
    if (last && sameGarden && close) {
      last.push(e);
    } else {
      groups.push([e]);
    }
  }
  return groups;
}

interface FeedGroupProps {
  events: FeedEvent[];
  onPhotoClick?: (photos: LightboxPhoto[], index: number) => void;
}

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days}j`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

export function FeedGroup({ events, onPhotoClick }: FeedGroupProps) {
  if (events.length === 0) return null;

  if (events.length === 1) {
    const e = events[0];
    return (
      <EventCard
        event={e}
        gardenName={e.garden_name ?? "Potager"}
        gardenId={e.garden_id}
        firstName={e.first_name}
        isDemo={e.is_demo}
        onPhotoClick={onPhotoClick}
      />
    );
  }

  const head = events[0];
  const name = head.first_name || "Un jardinier";
  const gardenName = head.garden_name ?? "Potager";

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-2">
        <span className="text-lg">&#127793;</span>
        <div className="flex-1 min-w-0">
          <p className="text-label-lg text-on-surface">
            <span className="font-semibold">{name}</span>
            {" sur "}
            <a href={`/galerie/${head.garden_id}`} className="text-primary hover:underline">
              {gardenName}
            </a>
            {head.is_demo && <DemoBadge className="ml-2 align-middle" />}
          </p>
          <p className="text-label-md text-on-surface-variant">
            {events.length} publications · {timeAgo(head.created_at)}
          </p>
        </div>
      </div>

      <div className="space-y-3 pl-3 border-l-2 border-surface-container-high">
        {events.map((e) => (
          <EventCard
            key={e.id}
            event={e}
            gardenName={gardenName}
            gardenId={e.garden_id}
            firstName={e.first_name}
            isDemo={e.is_demo}
            onPhotoClick={onPhotoClick}
          />
        ))}
      </div>
    </div>
  );
}
